import React, { useState, useEffect, useRef } from 'react';
import { SettingsProperty } from '../../engine/properties';

interface SettingsQuickFinderProps {
    isVisible: boolean;
    onClose: () => void;
    onSearchResultsSelected: (results: PropertySearchResult[], searchTerm: string) => void; // Changed from single property
    app: any; // EngineDemoApp instance
}

interface PropertySearchResult {
    property: SettingsProperty<any>;
    source: string; // 'AppSettings', 'SceneSettings', 'POIData', 'LayoutData'
    sourceName: string; // Display name
    path: string; // Property path like 'Camera.cameraDistance'
    matchScore: number; // Search relevance score
}

export const SettingsQuickFinder: React.FC<SettingsQuickFinderProps> = ({
    isVisible,
    onClose,
    onSearchResultsSelected,
    app
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<PropertySearchResult[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    // Focus input when component becomes visible
    useEffect(() => {
        if (isVisible && inputRef.current) {
            inputRef.current.focus();
            setSearchTerm('');
            setSelectedIndex(0);
        }
    }, [isVisible]);

    // Collect all properties from all sources
    const collectAllProperties = (): PropertySearchResult[] => {
        const results: PropertySearchResult[] = [];

        if (!app) return results;

        try {
            // 1. AppSettings Properties
            const appSettings = app.appSettings;
            if (appSettings) {
                const appProperties = app._getPropertiesFromObject(appSettings);
                appProperties.forEach((property: SettingsProperty<any>) => {
                    results.push({
                        property,
                        source: 'AppSettings',
                        sourceName: 'App Settings',
                        path: `${property.group || 'General'}.${property.name}`,
                        matchScore: 0
                    });
                });
            }

            // 2. SceneSettings Properties
            const sceneSettings = app.sceneSettings;
            if (sceneSettings) {
                const sceneProperties = app._getPropertiesFromObject(sceneSettings);
                sceneProperties.forEach((property: SettingsProperty<any>) => {
                    results.push({
                        property,
                        source: 'SceneSettings',
                        sourceName: 'Scene Settings',
                        path: `${property.group || 'General'}.${property.name}`,
                        matchScore: 0
                    });
                });
            }

            // 3. LayoutData Properties
            const layoutData = app.layoutData;
            if (layoutData) {
                const layoutProperties = app._getPropertiesFromObject(layoutData);
                layoutProperties.forEach((property: SettingsProperty<any>) => {
                    results.push({
                        property,
                        source: 'LayoutData',
                        sourceName: 'Layout Settings',
                        path: `${property.group || 'General'}.${property.name}`,
                        matchScore: 0
                    });
                });
            }

            // 4. Current POI Properties (if any POI is selected)
            const currentPOI = app.getCurrentPOI();
            if (currentPOI && currentPOI.poidata) {
                const poiProperties = app._getPropertiesFromObject(currentPOI.poidata);
                poiProperties.forEach((property: SettingsProperty<any>) => {
                    results.push({
                        property,
                        source: 'POIData',
                        sourceName: `POI: ${currentPOI.name}`,
                        path: `${property.group || 'General'}.${property.name}`,
                        matchScore: 0
                    });
                });
            }

        } catch (error) {
            console.warn('Error collecting properties:', error);
        }

        return results;
    };

    // Search and filter properties
    useEffect(() => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            setSelectedIndex(0);
            return;
        }

        const allProperties = collectAllProperties();
        const term = searchTerm.toLowerCase();

        // Calculate match scores
        const scored = allProperties.map(result => {
            let score = 0;
            const name = result.property.name.toLowerCase();
            const group = (result.property.group || '').toLowerCase();
            const path = result.path.toLowerCase();

            // Exact name match (highest priority)
            if (name === term) score += 100;
            else if (name.startsWith(term)) score += 80;
            else if (name.includes(term)) score += 60;

            // Group match
            if (group.includes(term)) score += 40;

            // Path match
            if (path.includes(term)) score += 20;

            // Partial word matches
            const nameWords = name.split(/[_\s]+/);
            const termWords = term.split(/[_\s]+/);
            
            termWords.forEach(termWord => {
                nameWords.forEach(nameWord => {
                    if (nameWord.startsWith(termWord)) score += 30;
                    else if (nameWord.includes(termWord)) score += 15;
                });
            });

            return { ...result, matchScore: score };
        });

        // Filter and sort by score
        const filtered = scored
            .filter(result => result.matchScore > 0)
            .sort((a, b) => b.matchScore - a.matchScore)
            .slice(0, 10); // Limit to top 10 results

        setSearchResults(filtered);
        setSelectedIndex(0);
    }, [searchTerm, app]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isVisible) return;

            switch (event.key) {
                case 'Escape':
                    event.preventDefault();
                    onClose();
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    setSelectedIndex(prev => 
                        prev < searchResults.length - 1 ? prev + 1 : prev
                    );
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (searchResults[selectedIndex]) {
                        const result = searchResults[selectedIndex];
                        onSearchResultsSelected(searchResults, searchTerm);
                        onClose();
                    }
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isVisible, searchResults, selectedIndex, onClose, onSearchResultsSelected, searchTerm]);

    if (!isVisible) return null;

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: '20vh'
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => {
                // Don't stop propagation for slider elements - they need global cleanup
                const target = e.target as HTMLElement;
                if (!target.closest('[data-slider-element]')) {
                    e.stopPropagation();
                }
            }}
            data-ui-element="quick-finder"
        >
            <div 
                style={{
                    backgroundColor: '#2d2d2d',
                    borderRadius: '12px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                    width: '600px',
                    maxHeight: '70vh',
                    overflow: 'hidden',
                    border: '1px solid #444'
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => {
                    // Don't stop propagation for slider elements - they need global cleanup
                    const target = e.target as HTMLElement;
                    if (!target.closest('[data-slider-element]')) {
                        e.stopPropagation();
                    }
                }}
            >
                {/* Search Input */}
                <div style={{
                    padding: '20px 20px 15px 20px',
                    borderBottom: '1px solid #444'
                }}>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search settings... (e.g. 'billboard', 'camera', 'opacity')"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px 16px',
                            fontSize: '16px',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #555',
                            borderRadius: '8px',
                            color: '#fff',
                            outline: 'none'
                        }}
                    />
                </div>

                {/* Search Results */}
                <div style={{
                    maxHeight: '50vh',
                    overflowY: 'auto'
                }}>
                    {searchResults.length === 0 && searchTerm.trim() && (
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#888',
                            fontSize: '14px'
                        }}>
                            No settings found for "{searchTerm}"
                        </div>
                    )}
                    
                    {searchTerm.trim() === '' && (
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#888',
                            fontSize: '14px'
                        }}>
                            Start typing to search settings...
                        </div>
                    )}

                    {searchResults.map((result, index) => (
                        <div
                            key={`${result.source}-${result.property.name}`}
                            style={{
                                padding: '12px 20px',
                                backgroundColor: index === selectedIndex ? '#444' : 'transparent',
                                borderLeft: index === selectedIndex ? '3px solid #007acc' : '3px solid transparent',
                                cursor: 'pointer',
                                borderBottom: '1px solid #333'
                            }}
                            onClick={() => {
                                onSearchResultsSelected(searchResults, searchTerm);
                                onClose();
                            }}
                            onMouseEnter={() => setSelectedIndex(index)}
                        >
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div>
                                    <div style={{
                                        color: '#fff',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        marginBottom: '2px'
                                    }}>
                                        {result.property.name}
                                    </div>
                                    <div style={{
                                        color: '#999',
                                        fontSize: '12px'
                                    }}>
                                        {result.path} • {result.sourceName}
                                    </div>
                                </div>
                                <div style={{
                                    backgroundColor: getSourceColor(result.source),
                                    color: '#fff',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                }}>
                                    {getSourceLabel(result.source)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '10px 20px',
                    backgroundColor: '#1a1a1a',
                    borderTop: '1px solid #333',
                    fontSize: '11px',
                    color: '#666',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                    <span>↑↓ Navigate • Enter Select • Esc Close</span>
                    <span>{searchResults.length} results</span>
                </div>
            </div>
        </div>
    );
};

function getSourceColor(source: string): string {
    switch (source) {
        case 'AppSettings': return '#007acc';
        case 'SceneSettings': return '#28a745';
        case 'POIData': return '#ffc107';
        case 'LayoutData': return '#6f42c1';
        default: return '#6c757d';
    }
}

function getSourceLabel(source: string): string {
    switch (source) {
        case 'AppSettings': return 'APP';
        case 'SceneSettings': return 'SCENE';
        case 'POIData': return 'POI';
        case 'LayoutData': return 'LAYOUT';
        default: return 'OTHER';
    }
} 